import _ from "lodash";
import React, { useState, useEffect, useRef } from "react";
import ACLStore from "lib/aclStore";

// components
import I18nHOC from "UIComponents/I18nHOC";
import UIModal from "UIComponents/UIModal";
import AssessmentIcon from "UIComponents/AssessmentIcon";
import AITutorChatWrapper from "AppComponents/AITutorChatWrapper";
import AITutorAnalysis from "AppComponents/AITutorAnalysis";
import CompactLEPreview from "AppComponents/CompactLEPreview";
import ScreenBaseComponent from "AppComponents/ScreenBaseComponent";
import FullScreenLoader from "UIComponents/FullScreenLoader/FullScreenLoader";

// design system
import {
  Button,
  Dialog,
  DSProvider,
  EmptyState,
  IconButton,
  Modal,
  Tooltip,
  PageHeader,
} from "@toddle-edu/ds-web";
import { NoDataIllustration } from "@toddle-edu/ds-theme";
import {
  ChevronLeftOutlined,
  CloseOutlined,
  CaretClosedFilled,
  RefreshArrowOutlined,
} from "@toddle-edu/ds-icons";

// third party
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import classNames from "classnames";

// utils
import {
  incrementSubmissionSession,
  areCheckpointsSame,
} from "./modules/Utils";
import {
  getCanGenerateAITutorPreview,
  getAssessmentFieldValue,
} from "Utils/AIUtils";
import { htmlToText, isSafari, isTouchableV3 } from "Utils";
import { formatDueDate } from "ClassRoom/modules/utils";
import { getIsAITutorModalitiesEnabled } from "Utils/ACLStoreUtils";

// modules
import { getOpeningMessageResourcesEnhancer } from "./modules/AITutorChatModalEnhancers";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { setGlobalTrayEnabledState } from "Login/modules/LoginModule";
import {
  onEndSession,
  createAITutorMessage,
} from "AppComponents/AITutorChatWrapper/modules/AITutorChatModule";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";
import { getAITutorConversationsEnhancer } from "AppComponents/AITutorChatWrapper/modules/AITutorChatEnhancers";
import {
  updateAIData,
  updateEvaluationData,
  deleteAssessmentToolResponse,
} from "ClassRoom/modules/ClassRoomModule";

import { updateAIConversation } from "modules/Services";
import { updateAIDataInRedux } from "Assessments/modules/AssessmentModule";

// constants
import { DIALOGS_INFO, MAX_SCREEN_WIDTH } from "./modules/Constants";
import { colors, fontStyle } from "Constants";

// scss
import classes from "./AITutorChatModal.scss";

const styles = {
  containerStyle: {
    height: "100%",
  },
  emptyStateTitle: {
    maxWidth: "280px",
  },
  modalHeaderStyle: {
    padding: "12px 16px",
  },
  modalBodyStyle: {
    padding: "8px 16px 12px 16px",
  },
  lowerConWrapperStyle: {
    padding: "16px 24px",
  },
  overlayInnerStyle: {
    maxWidth: "320px",
  },
  customTextViewStyle: {
    color: colors.textDefault,
    ...fontStyle.medium,
    fontSize: "1.4rem",
    lineHeight: "2rem",
  },
  tooltipOverlayInnerStyle: {
    maxWidth: "496px",
  },
  rotateBy90: { transform: "rotate(90deg)" },
};

const INITIAL_STATE = {
  DIALOG_ACTION: {
    action: "",
    params: {},
  },
};

const CustomHeader = props => {
  const { assessmentTitle, deadline, onCloseClick, t } = props;

  return (
    <div className={classes.headerContainer}>
      <div className={classes.flexColumn}>
        <Tooltip
          dsVersion={"2.0"}
          tooltip={assessmentTitle}
          placement={"bottom"}
          showArrow={true}
          visible={_.size(assessmentTitle) > 25}
          overlayInnerStyle={styles.overlayInnerStyle}
        >
          <h6 className={classNames(classes.aiTutorTitle)}>
            {assessmentTitle}
          </h6>
        </Tooltip>
        <div className={classNames("text-body", classes.subtext)}>
          {formatDueDate({ deadline, t })}
        </div>
      </div>
      <IconButton
        dsVersion={"2.0"}
        icon={<CloseOutlined />}
        variant="neutral"
        type="plain"
        onClick={onCloseClick}
        size="small"
        testId="instructionsPanel-close"
      />
    </div>
  );
};

const filterViewSteps = ["chatbotSettings"];
const filterViewFields = ["openingMessage"];

class AITutorChatModalWithRightPane extends ScreenBaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      shouldCloseOnOverlayClick: true,
    };
  }

  isFullScreenRightPane = () => {
    return true;
  };

  handleViewInstructions = () => {
    this.handleRightPane({ rightPane: "LE_PREVIEW" });
  };

  getRightPaneComponent = () => {
    const { currentRightPane } = this.state;
    const { assessmentId, t } = this.props;

    if (currentRightPane === "LE_PREVIEW") {
      return (
        <DSProvider dsVersion="2.0">
          <CompactLEPreview
            assessmentId={assessmentId}
            portalType={"PLANNER"}
            parentType={"VIEW_STEPS"}
            showFullScreenButton={false}
            onCloseClick={this.closeRightPane}
            attachmentItemHeight={100}
            canPerformActionsInAssignment={true}
            isDraftOrScheduledView={false}
            showPostSettings={false}
            customHeader={
              <CustomHeader onCloseClick={this.closeRightPane} t={t} />
            }
            filterViewSteps={filterViewSteps}
            filterViewFields={filterViewFields}
            lowerConWrapperStyle={styles.lowerConWrapperStyle}
            customTextViewStyle={styles.customTextViewStyle}
          />
        </DSProvider>
      );
    }
    return null;
  };

  render() {
    const { currentRightPane } = this.state;
    return (
      <UIModal isFullScreen={true} isOpen={true}>
        <AITutorChatModal
          {...this.props}
          handleViewInstructions={this.handleViewInstructions}
        />

        {currentRightPane && this.renderRightPane()}
      </UIModal>
    );
  }
}

const AITutorChatModal = props => {
  const {
    t,
    entityId,
    conversationEntityType = "STUDENT_ASSIGNMENT",
    course,
    onClose,
    aiConversationId,
    messages,
    initialEntitySubType,
    isSubmitted,
    onSubmitWork,
    assessmentId,
    assignmentId,
    studentAssignmentData,
    isStaff,
    isStudent,
    source,
    onEndSession,
    createAITutorMessage,
    assessmentDetails,
    analysisToolEvaluation,
    isParent,
    updateAIData,
    updateEvaluationData,
    deleteAssessmentToolResponse,
    updateAIDataInRedux,
    assessmentRequiredFieldValues,
    metadataRequiredFieldValues,
    updateAIConversation,
    conversationMetadata,
    showAIAnalysisLoader,
    currentAIState,
    learningCourseId,
    showCompactUI,
    handleViewInstructions,
    openingMessageResources,
    isLoadingOpeningMessageResources,
    isData,
    isLoading,
  } = props;

  const testId = `assessment-aiTutorChatModal${
    source ? `-${_.lowerCase(source)}` : ""
  }`;

  const isAITutorModalitiesEnabled = getIsAITutorModalitiesEnabled();

  // states
  const [currentDialogAction, setCurrentDialogAction] = useState(
    INITIAL_STATE.DIALOG_ACTION
  );

  const [generateChatObj, setGenerateChatObj] = useState({
    shouldGeneratePreviewChat: !aiConversationId,
    entitySubType: initialEntitySubType,
  });

  const [showAnalysis, setShowAnalysis] = useState(false);

  const [isAIGeneratingResponse, setIsAIGeneratingResponse] = useState(false);
  const [hasSelectedModality, setHasSelectedModality] = useState(false);
  const [showCollapsedAnalysis, setShowCollapsedAnalysis] = useState(false);
  const [showChatResetDialog, setShowChatResetDialog] = useState(false);
  const [showFullScreenLoader, setShowFullScreenLoader] = useState(false);

  // refs
  const hasHandledResetRef = useRef(false);
  const footerRef = useRef(null);

  const onSendMessage = footerRef?.current?.onSendMessage;

  useEffect(() => {
    updateAIData({ mathEquationLatex: "" });

    return () => {
      updateAIData({ mathEquationLatex: "" });
    };
  }, []);

  useEffect(() => {
    if (!hasSelectedModality) {
      setShowCollapsedAnalysis(false);
    }
  }, [hasSelectedModality]);

  const initialGlobalTrayState = useRef(props.isGlobalTrayEnabled);

  useEffect(() => {
    const { setGlobalTrayEnabledState } = props;
    const initialTrayState = initialGlobalTrayState.current;

    setGlobalTrayEnabledState(false);

    return () => {
      setGlobalTrayEnabledState(initialTrayState);
    };
  }, []);

  const isSubmissionStateLocked =
    _.get(studentAssignmentData, "lockingState.state") === "LOCKED";

  const isSubmissionClosed = _.get(conversationMetadata, "isSubmissionClosed");

  const isSubmissionLockedForStudent = isSubmissionStateLocked || isSubmitted;
  const isConversationEvaluated = !_.isEmpty(
    _.first(analysisToolEvaluation)?.toolResponses
  );

  // Show analysis when conversation is evaluated
  useEffect(() => {
    if (isConversationEvaluated) {
      setShowAnalysis(true);
    }
  }, [isConversationEvaluated]);

  const _messages = messages?.edges || messages;

  const hasUserMessage =
    !_.isNil(_messages) &&
    _.some(_messages, ({ node }) => node?.authorType === "user");

  const mode =
    isParent ||
    isSubmissionLockedForStudent ||
    isConversationEvaluated ||
    showAIAnalysisLoader
      ? "view"
      : "edit";

  const _isLoading = !isData && isLoading;

  // if tutor is updated then
  // 1. if user conversation exists then show dialog box to reset the chat
  // 2. if user conversation does not exist then reset the chat directly

  useEffect(() => {
    if (
      _isLoading ||
      (isStudent && isSubmitted) ||
      isParent ||
      isSubmissionLockedForStudent ||
      isAIGeneratingResponse ||
      showAIAnalysisLoader ||
      !_.isEmpty(currentAIState) ||
      isLoadingOpeningMessageResources ||
      hasHandledResetRef.current
    ) {
      return;
    }

    // 1. check if any content of the required fields is updated like title, description, etc.
    const normalizeImgTags = val =>
      _.cloneDeepWith(val, v =>
        typeof v === "string"
          ? _.replace(
              v,
              /<img\s[^>]*?src="([^"]*)"[^>]*?\/?>/gi,
              '<img src="$1">'
            )
          : undefined
      );

    const isContentUpdated =
      !_.isEmpty(metadataRequiredFieldValues) &&
      !_.isEmpty(assessmentRequiredFieldValues) &&
      _.size(metadataRequiredFieldValues) ===
        _.size(assessmentRequiredFieldValues) &&
      !_.isEqual(
        normalizeImgTags(metadataRequiredFieldValues),
        normalizeImgTags(assessmentRequiredFieldValues)
      );

    // 2. when modalities are enabled, check if any modality attachment in opening message is updated, deleted or added
    const copiedOpeningMessageResources = _.get(
      _.find(_messages, ({ node }) => {
        return _.get(node, "metadata.isOpeningMessage", false);
      }),
      "node.attachments"
    );

    const isModalityUpdated = (() => {
      if (
        !getIsAITutorModalitiesEnabled() ||
        !aiConversationId ||
        _.isUndefined(_messages)
      ) {
        return false;
      }

      // Handle empty or undefined arrays
      if (
        _.isEmpty(openingMessageResources) &&
        _.isEmpty(copiedOpeningMessageResources)
      ) {
        return false;
      }

      if (
        _.isEmpty(openingMessageResources) ||
        _.isEmpty(copiedOpeningMessageResources)
      ) {
        return true;
      }

      return _.some(openingMessageResources, originalAttachment => {
        const matchingCopiedAttachment = _.find(copiedOpeningMessageResources, {
          type: originalAttachment.type,
        });

        if (!matchingCopiedAttachment) return true;

        const attachmentType = originalAttachment.type;

        switch (attachmentType) {
          case "CODE_EDITOR":
          case "TODDLE_DOC": {
            const originalContent = _.get(
              originalAttachment,
              "modalityAttachment.content"
            );
            const copiedContent = _.get(
              matchingCopiedAttachment,
              "modalityAttachment.content"
            );

            const originalLanguage = _.get(
              originalAttachment,
              "modalityAttachment.metadata.language"
            );
            const copiedLanguage = _.get(
              matchingCopiedAttachment,
              "modalityAttachment.metadata.language"
            );

            if (attachmentType === "CODE_EDITOR") {
              return (
                !_.isEqual(originalContent, copiedContent) ||
                !_.isEqual(originalLanguage, copiedLanguage)
              );
            } else {
              const originalContentPlain = htmlToText(originalContent, {});
              const copiedContentPlain = htmlToText(copiedContent, {});
              return !_.isEqual(originalContentPlain, copiedContentPlain);
            }
          }

          case "WORKBOOK":
          case "LINK": {
            const isLinkChanged =
              originalAttachment?.url &&
              originalAttachment?.url !== matchingCopiedAttachment?.url;

            if (attachmentType === "LINK") {
              return (
                isLinkChanged ||
                !areCheckpointsSame(
                  originalAttachment,
                  matchingCopiedAttachment
                )
              );
            }

            return isLinkChanged;
          }

          default:
            return false;
        }
      });
    })();

    if (isContentUpdated || isModalityUpdated) {
      hasHandledResetRef.current = true;
      if (hasUserMessage) {
        setCurrentDialogAction({ action: "RESET_CHAT", params: {} });
      } else {
        onResetChat();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    aiConversationId,
    isAIGeneratingResponse,
    hasUserMessage,
    metadataRequiredFieldValues,
    assessmentRequiredFieldValues,
    isLoadingOpeningMessageResources,
    openingMessageResources,
    _isLoading,
  ]);

  // functions

  const onResetChat = () => {
    setGenerateChatObj(prevState => ({
      ...prevState,
      entitySubType: generateChatObj?.entitySubType,
      shouldGeneratePreviewChat: true,
      shouldResetChat: true,
    }));
  };

  const onHideChatResetDialog = () => {
    setShowChatResetDialog(false);
  };

  const onEndSessionLocal = async params => {
    // send any pending message before ending the session
    if (_.isFunction(onSendMessage) && isAITutorModalitiesEnabled) {
      updateEvaluationData({ showAIAnalysisLoader: true });
      await onSendMessage({ skipAssistantMessage: true });
    }

    await onEndSession({
      ...params,
      course,
      entityId,
      aiConversationId,
      assessmentDetails,
      messages,
      assignmentId,
      learningCourseId,
    });
  };

  const onClickNewSession = () => {
    // student will have new sessions like SESSION_1, SESSION_2
    // staff will have the same session "STUDENT_PREVIEW"
    let newEntitySubType = _.get(generateChatObj, "entitySubType");

    if (!isStaff) {
      newEntitySubType = incrementSubmissionSession(newEntitySubType);
    }

    setShowAnalysis(false);

    setGenerateChatObj(prevState => ({
      ...prevState,
      entitySubType: newEntitySubType,
      shouldGeneratePreviewChat: true,
      shouldResetChat: isStudentPreview,
    }));
  };

  const handleAIResponseChange = value => {
    setIsAIGeneratingResponse(value);
  };

  const handleModalityStateChange = hasModality => {
    setHasSelectedModality(hasModality);
  };

  // reset dialog state when user clicks the first button in dialog
  const onCancelDialogClick = () => {
    setCurrentDialogAction({
      action: "",
      params: {},
    });
  };

  // run the respective action based on currentDialogAction when user clicks the second button in dialog
  const onConfirmDialogClick = async () => {
    onCancelDialogClick();

    switch (currentDialogAction?.action) {
      case "START_NEW_CHAT": {
        setShowAnalysis(true);
        await onClickNewSession();
        break;
      }
      case "END_SESSION": {
        setShowAnalysis(true);
        await onEndSessionLocal();
        break;
      }
      case "RESET_CHAT": {
        if (!isAITutorModalitiesEnabled) setShowAnalysis(true);
        onResetChat();
      }
    }
  };

  const onCloseDialogClick = async () => {
    switch (currentDialogAction?.action) {
      case "RESET_CHAT": {
        const lastMessageAuthorType = _.get(
          _.last(messages),
          "node.authorType"
        );

        if (lastMessageAuthorType !== "tutorUpdatedWithResetChat") {
          await createAITutorMessage({
            variables: {
              input: {
                conversationId: aiConversationId,
                authorType: "tutorUpdatedWithResetChat",
                content: "ai:tutor_updated_by_your_teacher",
                metadata: {},
              },
            },
          });
        }

        onCancelDialogClick();
        break;
      }
      default: {
        onCancelDialogClick();
      }
    }
  };

  const onClickEndSession = () => {
    setCurrentDialogAction({ action: "END_SESSION", params: {} });
  };

  const onClickStartNewChat = () => {
    setCurrentDialogAction({ action: "START_NEW_CHAT", params: {} });
  };

  const onContentCreationComplete = () => {
    setGenerateChatObj(prevState => ({
      ...prevState,
      shouldGeneratePreviewChat: false,
      shouldResetChat: false,
    }));
  };

  const onSubmitWorkLocal = async () => {
    if (!isConversationEvaluated) {
      await onEndSessionLocal({ addActivityLog: false });
    }

    onSubmitWork();
    onClose();
  };

  const onCloseSubmissionModal = () => {
    updateAIConversation({
      input: {
        id: aiConversationId,
        metadata: {
          ...(conversationMetadata || {}),
          isSubmissionClosed: false,
        },
      },
    });
  };

  const onToggleAnalysisContainer = () => {
    setShowCollapsedAnalysis(prevState => !prevState);
  };

  const onResetStudentPreview = async () => {
    // send any pending message before ending the session
    if (_.isFunction(onSendMessage) && isAITutorModalitiesEnabled) {
      setShowFullScreenLoader(true);
      await onSendMessage({ skipAssistantMessage: true });
      setShowFullScreenLoader(false);
    }

    onResetChat();
    setShowAnalysis(false);
    setShowChatResetDialog(false);
  };

  // variables

  const { action = "" } = currentDialogAction || {};

  let tutorFields;

  if (
    isAITutorModalitiesEnabled &&
    ACLStore.can("FeatureFlag:GenerateAITutorPreviewWithMinimalFields")
  ) {
    tutorFields = ["openingMessage", "aiRules"];
  }

  const canGenerateTutorPreview = getCanGenerateAITutorPreview({
    assessmentDetails,
    tutorFields,
  });

  const isStudentPreview = source === "STUDENT_PREVIEW";
  const showEmptyState = isStudentPreview && !canGenerateTutorPreview;

  const showSubmissionClosedModal =
    isSubmissionClosed && !isSubmitted && !isParent && isSubmissionStateLocked;

  const shouldShowAnalysis = isAITutorModalitiesEnabled
    ? showAnalysis
    : !showCompactUI || showAnalysis;

  const headerRight =
    !isStaff && !isSubmissionLockedForStudent && mode === "view" ? (
      <Button
        variant={"progressive"}
        type={"fill"}
        size={"medium"}
        dsVersion={"2.0"}
        onClick={onSubmitWorkLocal}
        disabled={
          !hasUserMessage ||
          showAIAnalysisLoader ||
          isParent ||
          isSubmissionLockedForStudent ||
          !isConversationEvaluated
        }
        testId={`${testId}-submitButton`}
      >
        {t("common:submit")}
      </Button>
    ) : null;

  const title = isAITutorModalitiesEnabled
    ? t("ai:student_preview_not_ready_yet")
    : t("ai:ai_tutor_student_preview_empty_state_msg");

  const subtitle = isAITutorModalitiesEnabled
    ? t("ai:student_preview_not_ready_yet_description")
    : null;

  const onClickGotoSetup = () => {
    const { isValid } = props;

    if (_.isFunction(isValid)) {
      updateAIDataInRedux({ aiTutorSelectedTab: "TASK_DETAILS" });
      setTimeout(() => isValid(false), 100);
    }

    onClose();
  };

  const onRegenerateClick = async () => {
    setShowAnalysis(true);

    const toolResponsesToDelete = _.map(
      _.first(analysisToolEvaluation)?.toolResponses,
      "id"
    );

    if (!_.isEmpty(toolResponsesToDelete)) {
      await deleteAssessmentToolResponse({
        input: { ids: toolResponsesToDelete },
      });
    }

    await onEndSessionLocal();
  };

  const secondaryButton = isAITutorModalitiesEnabled ? (
    <Button onClick={onClickGotoSetup}>{t("ai:go_to_configure")}</Button>
  ) : null;

  const renderHeader = () => {
    const isEditMode = mode === "edit";
    const isOpeningMessageOnly = _.every(
      messages,
      ({ node }) => node?.metadata?.isOpeningMessage
    );
    const isInitialState = _.isEmpty(messages); // 0 messages

    const assignmentTitle = getAssessmentFieldValue({
      fields: assessmentDetails?.allFields,
      uid: "title",
    });

    let title = null;

    if (!_.isEmpty(_.trim(assignmentTitle))) {
      title = assignmentTitle;
    } else if (!_.isEmpty(assessmentDetails)) {
      title = t("common:untitled");
    }

    const disableButton =
      isInitialState ||
      isOpeningMessageOnly ||
      _.some(messages, ({ node }) => _.includes(node?.id, "NEW_")) ||
      isAIGeneratingResponse ||
      showAIAnalysisLoader;

    const icon = !isStudentPreview ? (
      <AssessmentIcon
        assessmentType={assessmentDetails?.assessmentType?.value}
      />
    ) : null;

    const titleClass = classNames({
      [classes.assessmentLabel]: isStudentPreview,
      ["text-body-s"]: isStudentPreview,
    });

    const tutorLabelClass = classNames(classes.assessmentLabel, "text-body-s");

    return (
      <PageHeader>
        <PageHeader.Left
          onBackClick={onClose}
          title={
            title ? (
              <div className={classes.chatTitleCon}>
                {isStudentPreview ? <h6>{t("common:student_view")}</h6> : null}

                <Tooltip
                  dsVersion="2.0"
                  tooltip={title}
                  showArrow={true}
                  placement="bottom"
                  visible={_.size(title) > 25}
                  overlayInnerStyle={styles.tooltipOverlayInnerStyle}
                >
                  <h6 className={titleClass}>{title}</h6>
                </Tooltip>

                {!isStudentPreview ? (
                  <div className={tutorLabelClass}>{t("common:ai_tutor")}</div>
                ) : null}
              </div>
            ) : null
          }
          icon={icon}
        />
        <PageHeader.Right>
          {!showEmptyState ? (
            <div className={classes.chatSubHeaderRight}>
              {source === "STUDENT_PREVIEW" && isAITutorModalitiesEnabled ? (
                <Tooltip dsVersion="2.0" tooltip={t("classRoom:clear_chat")}>
                  <IconButton
                    dsVersion="2.0"
                    icon={<RefreshArrowOutlined />}
                    variant="neutral"
                    type="outlined"
                    onClick={() => setShowChatResetDialog(true)}
                    size="medium"
                    testId={`${testId}-resetChatButton`}
                    disabled={disableButton}
                  />
                </Tooltip>
              ) : null}

              {handleViewInstructions ? (
                <Button
                  dsVersion="2.0"
                  size="medium"
                  variant="neutral"
                  type="outlined"
                  onClick={handleViewInstructions}
                >
                  {t("common:view_instructions")}
                </Button>
              ) : null}

              {isEditMode ? (
                <Button
                  dsVersion="2.0"
                  size="medium"
                  variant="primary"
                  disabled={disableButton}
                  onClick={onClickEndSession}
                  testId={`${testId}-endSession`}
                >
                  {t("ai:end_session")}
                </Button>
              ) : null}

              {headerRight}
            </div>
          ) : null}
        </PageHeader.Right>
      </PageHeader>
    );
  };

  const renderCollapsedAIAnalysis = () => {
    return (
      <div className={classes.collapsedHeaderContainer}>
        <IconButton
          dsVersion="2.0"
          variant="neutral"
          type="plain"
          size="small"
          icon={<CaretClosedFilled style={styles.rotateBy90} />}
          onClick={onToggleAnalysisContainer}
          testId={`${testId}-toggleIcon`}
        />
        <div className={classNames("text-body", classes.labelContainer)}>
          {t("ai:ai_analysis")}
        </div>
      </div>
    );
  };

  const renderAnalysis = () => {
    return showCollapsedAnalysis ? (
      renderCollapsedAIAnalysis()
    ) : (
      <div className={tutorAnalysisContainerClass}>
        <AITutorAnalysis
          aiConversationId={aiConversationId}
          assessmentId={assessmentId}
          onClickStartNewSession={
            !isSubmissionLockedForStudent && !isParent
              ? onClickStartNewChat
              : null
          }
          showLoader={showAIAnalysisLoader}
          source={source}
          currentAIState={currentAIState}
          isStartNewChatDisabled={generateChatObj?.shouldGeneratePreviewChat}
          hasSelectedModality={hasSelectedModality}
          onToggleAnalysisContainer={onToggleAnalysisContainer}
          onRegenerateClick={onRegenerateClick}
        />
      </div>
    );
  };

  // classes
  const bodyContainerClass = classNames(classes.bodyContainer, {
    [classes.bodyContainerV2]: isAITutorModalitiesEnabled,
    [classes.bodyContainerCompact]: showCompactUI,
    [classes.bodyContainerCompactChatOnly]:
      showCompactUI && !shouldShowAnalysis,
    [classes.revampBodyContainer]:
      isAITutorModalitiesEnabled && (hasSelectedModality || showEmptyState),
  });

  const aiTutorChatConClass = classNames(classes.aiTutorChatCon, {
    [classes.aiTutorChatConCompact]: showCompactUI && !shouldShowAnalysis,
    [classes.aiTutorChatConCompactWithAnalysis]:
      showCompactUI && shouldShowAnalysis,
    [classes.revampAITutorChatCon]: isAITutorModalitiesEnabled,
  });

  const tutorAnalysisContainerClass = classNames(
    classes.tutorAnalysisContainer,
    {
      [classes.tutorAnalysisContainerSafari]: isSafari(),
      [classes.tutorAnalysisContainerCompact]: showCompactUI,
    }
  );

  return (
    <>
      {isAITutorModalitiesEnabled ? renderHeader() : null}

      <div className={bodyContainerClass}>
        {!showCompactUI && !isAITutorModalitiesEnabled ? (
          <IconButton
            dsVersion={"2.0"}
            icon={<ChevronLeftOutlined />}
            variant={"neutral"}
            type={"outlined"}
            size={"medium"}
            onClick={onClose}
            className={classes.backButton}
            testId={`${testId}-backButton`}
          />
        ) : null}

        {!showEmptyState ? (
          <>
            <div className={aiTutorChatConClass}>
              <AITutorChatWrapper
                containerStyle={styles.containerStyle}
                leId={assessmentId}
                entityId={entityId}
                conversationEntityType={conversationEntityType}
                course={course}
                aiConversationId={aiConversationId}
                mode={mode}
                onClickEndSession={onClickEndSession}
                showTabs={false}
                source={source}
                onContentCreationComplete={onContentCreationComplete}
                headerRight={headerRight}
                learningCourseId={learningCourseId}
                showCompactUI={showCompactUI}
                onClose={onClose}
                handleViewInstructions={handleViewInstructions}
                onAIGeneratingChange={handleAIResponseChange}
                onModalityStateChange={handleModalityStateChange}
                footerRef={footerRef}
                {...generateChatObj}
              />
            </div>

            {shouldShowAnalysis ? renderAnalysis() : null}
          </>
        ) : (
          <EmptyState
            dsVersion={"2.0"}
            titleStyle={styles.emptyStateTitle}
            subtitleStyle={styles.emptyStateTitle}
            illustration={NoDataIllustration}
            title={title}
            subtitle={subtitle}
            secondaryButton={secondaryButton}
          />
        )}
      </div>
      {/* dialog box to render start new chat and end session warnings */}
      {!_.isEmpty(action) ? (
        <Dialog
          dsVersion={"2.0"}
          variant="delete-dialog"
          title={DIALOGS_INFO?.[action]?.title?.({ t })}
          message={DIALOGS_INFO?.[action]?.message?.({ t })}
          buttonOneText={DIALOGS_INFO?.[action]?.buttonOneText?.({ t })}
          buttonTwoText={DIALOGS_INFO?.[action]?.buttonTwoText?.({ t })}
          onButtonOneClick={onCloseDialogClick}
          onButtonTwoClick={onConfirmDialogClick}
          setIsDialogOpen={true}
          onClose={onCloseDialogClick}
          testId={`${testId}-dialog`}
        />
      ) : null}

      {showSubmissionClosedModal ? (
        <Modal
          dsVersion="2.0"
          onClose={onCloseSubmissionModal}
          width="358px"
          height="180px"
        >
          <Modal.Header
            isClosable={true}
            hasSeparator={false}
            title={t("classRoom:submission_closed")}
            style={styles.modalHeaderStyle}
          />
          <Modal.Body style={styles.modalBodyStyle}>
            <div className="text-body">
              {t("ai:ai_tutor_submission_closed_warning_msg")}
            </div>
          </Modal.Body>
        </Modal>
      ) : null}

      {showChatResetDialog ? (
        <Dialog
          dsVersion="2.0"
          variant="delete-dialog"
          title={t("classRoom:clear_chat")}
          message={t("classRoom:clear_chat_history_msg")}
          buttonOneText={t("common:cancel")}
          buttonTwoText={t("common:clear")}
          onButtonOneClick={onHideChatResetDialog}
          onButtonTwoClick={onResetStudentPreview}
          onClose={onHideChatResetDialog}
        />
      ) : null}

      {showFullScreenLoader ? <FullScreenLoader /> : null}
    </>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { aiConversation, courseId, course: _course } = ownProps;
  const { id: aiConversationId, entitySubType = "SESSION_1" } =
    aiConversation?.node || {};

  const { showAIAnalysisLoader, aiAnalysisState: currentAIState } = _.get(
    state,
    "classRoom.evaluation",
    {}
  );

  const academicYearId = getSelectedAcademicYearId(state);
  const userInfo = getUserInfo();

  const userId = userInfo.id;
  const userType = userInfo.user_type;

  const isStaff = userType === "staff";
  const isStudent = userType === "student";
  const isParent = userType === "parent";

  const isGlobalTrayEnabled = _.get(state, "login.isGlobalTrayEnabled", true);

  const isTouchableDevice = isTouchableV3(MAX_SCREEN_WIDTH);

  // Check for landscape mode on touchable devices (iPads)
  const isLandscape = window.innerWidth > window.innerHeight;
  const isScreenNarrow = window.innerWidth <= MAX_SCREEN_WIDTH;

  // Show compact UI for:
  // 1. Portrait mode on narrow screens with touch capability
  // 2. Landscape mode on touchable devices (iPads)
  const showCompactUI =
    !isStaff && isTouchableDevice && (isScreenNarrow || isLandscape);

  const course = !_.isEmpty(_course)
    ? _course
    : getCourseDetailsFromCache({
        id: courseId,
        courseGradeFilters: { academicYearId },
      });

  return {
    aiConversationId,
    initialEntitySubType: entitySubType,
    userId,
    isStaff,
    isStudent,
    isParent,
    course,
    showAIAnalysisLoader,
    currentAIState,
    showCompactUI,
    isGlobalTrayEnabled,
    fetchPolicy: "cache-first",
  };
};

const mapActionCreators = {
  onEndSession,
  createAITutorMessage,
  updateAIData,
  deleteAssessmentToolResponse,
  updateAIDataInRedux,
  updateAIConversation,
  setGlobalTrayEnabledState,
  updateEvaluationData,
};

AITutorChatModalWithRightPane.defaultProps = {
  rightPaneWidth: 480,
  shouldCloseOnOverlayClick: true,
  rightPaneOverlayStyle: {
    opacity: 0.15,
  },
};

export default compose(
  I18nHOC({ resource: ["ai", "common"] }),
  connect(mapStateToProps, mapActionCreators),
  getOpeningMessageResourcesEnhancer,
  getAITutorConversationsEnhancer
)(AITutorChatModalWithRightPane);
